// A single member card. Paints immediately; role pills hydrate lazily to mirror
// the real two-step fetch (GET /api/users then GET /api/users/{id}/roles).
import { useState, useEffect } from "react";
import { Box, Typography, Button, Skeleton, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { MONO_FONT } from "../data/mockUsersRoles";
import { RolePill, ScopePill, SourceBadge, UserAvatar } from "./shared";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";

const UserCard = ({ user, index = 0, onEdit, onRemove }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rolesReady, setRolesReady] = useState(false);

  // BFF call: GET /api/users/{id}/roles  (see docs/users-roles-api.md §2.2)
  // Lazy, staggered hydration — capped so a large realm doesn't fan out at once.
  useEffect(() => {
    const delay = 200 + (index % 6) * 110;
    const t = setTimeout(() => setRolesReady(true), delay);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <Box
      sx={{
        backgroundColor: "var(--fx-paper)",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: colors.primary[300],
          boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
        },
      }}
    >
      <UserAvatar user={user} size={38} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)', lineHeight: 1.3 }}>
          {user.name}
          <SourceBadge federated={user.federated} />
        </Typography>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            color: colors.grey[500],
            mb: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user.email}
        </Typography>

        {/* Role pills (lazy) */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, minHeight: 20 }}>
          {rolesReady ? (
            user.roles.map((r) => <RolePill key={r} roleKey={r} />)
          ) : (
            <Skeleton variant="rounded" width={72} height={18} sx={{ borderRadius: "5px" }} />
          )}
        </Box>

        {/* Project / site scopes */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
          {user.projects.length ? (
            user.projects.map((p) => <ScopePill key={p} label={p} />)
          ) : (
            <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500] }}>
              No site scope
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.5625rem", color: colors.grey[500] }}>
            Since {user.since}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onEdit(user)}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 13 }} />}
              sx={{ minHeight: 26, py: 0.25, px: 1, fontSize: "0.6875rem" }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onRemove(user)}
              aria-label={`Remove ${user.name}`}
              sx={{
                minHeight: 26,
                minWidth: 28,
                py: 0.25,
                px: 0.75,
                color: "#dc2626",
                borderColor: "rgba(220,38,38,0.3)",
                "&:hover": { borderColor: "#dc2626", backgroundColor: "rgba(220,38,38,0.06)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserCard;
